import { useEffect, useState } from "react";
import { API } from "@/lib/api";

/**
 * Renders an authenticated image from the protected /api/files/view endpoint.
 * Uses fetch+blob so the Authorization header is sent properly (img src can't
 * send headers, so we either fetch as blob or pass ?auth=).
 */
export function fileUrl(path, { w, download } = {}) {
  const token = localStorage.getItem("fl_token");
  const q = new URLSearchParams({ path });
  if (w) q.set("w", w);
  if (download) q.set("download", "1");
  if (token) q.set("auth", token);
  return `${API}/files/view?${q}`;
}

// Resized, browser-cached image straight from the server (fast gallery tiles).
function SizedImage({ path, alt, className, width, eager, plain }) {
  const [state, setState] = useState("loading");
  return (
    <span className={`relative block overflow-hidden ${plain ? "" : "bg-zinc-200 dark:bg-zinc-800"} ${className}`}>
      {state === "loading" && !plain && <span className="absolute inset-0 animate-pulse bg-zinc-200 dark:bg-zinc-800" />}
      {state === "error" && <span className="absolute inset-0 flex items-center justify-center text-[10px] text-zinc-500">Image unavailable</span>}
      <img src={fileUrl(path, { w: width })} alt={alt} loading={eager ? "eager" : "lazy"} decoding="async" draggable={false}
        onLoad={() => setState("ok")} onError={() => setState("error")}
        className={`w-full h-full object-cover transition-opacity duration-300 ${state === "ok" ? "opacity-100" : "opacity-0"}`} />
    </span>
  );
}

export function AuthImage({ path, alt = "", className = "", width, eager, plain }) {
  if (width) return <SizedImage path={path} alt={alt} className={className} width={width} eager={eager} plain={plain} />;
  return <BlobImage path={path} alt={alt} className={className} />;
}

function BlobImage({ path, alt, className }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let revoked = false;
    let blobUrl = null;
    const token = localStorage.getItem("fl_token");
    const url = `${API}/files/view?path=${encodeURIComponent(path)}${token ? `&auth=${token}` : ""}`;

    fetch(url, { credentials: "include" })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(r.statusText))))
      .then((blob) => {
        if (revoked) return;
        blobUrl = URL.createObjectURL(blob);
        setSrc(blobUrl);
      })
      .catch(() => {});

    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [path]);

  if (!src) {
    return (
      <div className={`bg-zinc-200 animate-pulse ${className}`} />
    );
  }
  return <img src={src} alt={alt} className={className} loading="lazy" />;
}

export function PhotoGrid({ paths, onClick }) {
  if (!paths || paths.length === 0) return null;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
      {paths.map((p, i) => (
        <button
          key={p}
          type="button"
          onClick={() => onClick?.(p, i)}
          className="aspect-square overflow-hidden border border-zinc-300 hover:border-[#FF5F15] transition-colors"
          data-testid={`photo-thumb-${i}`}
        >
          <AuthImage path={p} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
        </button>
      ))}
    </div>
  );
}
